import { useEffect } from 'react'
import { updateUser } from 'api/user'
import { addRelation, deleteRelation } from 'api/relations'
import NetworkCard from 'components/NetworkCard'
import CreateCardPlaceholder from 'components/CreateCardPlaceholder'
import Button from 'components/UI/Button'
import useRelations from 'hooks/api/useRelations'
import useUsers from 'hooks/api/useUsers'
import useTodo from 'hooks/useTodo'
import { t, wait } from 'helpers'
import { getUser } from 'helpers/storage'
import { notif } from 'helpers/notif'
import { useAppStore, useModalStore, useUserStore } from 'store'
import { Relation } from 'types/app'
import { DASHBOARD_SECTIONS, RELATIONS } from 'types/enums'

interface Props {
	setInvitesCount: (count: number) => void
}

const AgencyInvites = ({ setInvitesCount }: Props): JSX.Element => {
	const { data, refresh } = useRelations(
		RELATIONS.AGENCY,
		getUser()?.['Custom:User:Id'] || '',
		'Customer',
		'/Lookup',
	)

	const { users } = useUsers(
		(data as Relation[])
			?.filter((rel) => rel?.status !== 'Rejected')
			?.map((rel) => rel.sourceId),
	)

	useEffect(() => {
		setInvitesCount(users?.length || 0)
	}, [users?.length, setInvitesCount])

	useTodo(
		users?.length,
		2,
		'APP_USER_INVITES_LABEL',
		DASHBOARD_SECTIONS.NETWORK,
	)

	return (
		<>
			{users?.length ? (
				<div className="responsiveGrid">
					{users?.map((user) => (
						<NetworkCard
							key={user.ContentId}
							data={user}
							onClick={() => {
								const { user: currentUser } = useUserStore.getState()
								const { setModal } = useModalStore.getState()
								setModal({
									show: true,
									children: (
										<div
											className="card"
											style={{
												maxWidth: '750px',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												gap: '20px',
												padding: '20px 50px',
											}}>
											<>
												<h4 className="h4-m" style={{ textAlign: 'center' }}>
													{t('APP_CONFIRM_USER_INVITE')}
												</h4>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '10px',
													}}>
													<Button
														style={{ width: '100%' }}
														onClick={() => {
															const { setUserInfoSidebar } =
																useAppStore.getState()
															setUserInfoSidebar(user?.ContentId)
															setModal(null)
														}}>
														{t('APP_OPEN_PROFILE')}
													</Button>
													<Button
														style={{ width: '100%' }}
														variant="themed"
														onClick={async () => {
															const poolId =
																useUserStore.getState()?.user
																	?.CustomerPoolDefault
															await addRelation({
																relation: RELATIONS.ORGANIZATION,
																type: 'Customer',
																sourceId: user?.ContentId,
																targetId: poolId || '',
																query: `?emailTemplateId=GenericMessage&subject=${t(
																	'APP_MESSAGE_AGENCY_APPROVE_SUBJECT',
																).replace(
																	'[YourName]',
																	currentUser?.ContentTitle || '',
																)}&message=${encodeURIComponent(
																	t('APP_MESSAGE_AGENCY_APPROVE_BODY')
																		.replace(
																			'[RecipientName]',
																			user?.ContentTitle,
																		)
																		.replace(
																			'[YourName]',
																			currentUser?.ContentTitle || '',
																		),
																)}`,
															})
															await deleteRelation(
																user?.ContentId,
																'Customer',
																RELATIONS.AGENCY,
																getUser()?.['Custom:User:Id'] || '',
															)
															await deleteRelation(
																getUser()?.['Custom:User:Id'] || '',
																'Customer',
																RELATIONS.GROUP_INVITE,
																user?.ContentId,
															)
															await updateUser(user?.ContentId, [
																{
																	Name: 'Customer:Agency:Status',
																	Value: 'Approved',
																},
															])
															setModal(null)
															await wait(1000)
															refresh()
															notif('success', t('APP_INVITE_ACCEPTED'))
														}}>
														{t('APP_CONFIRM')}
													</Button>
													<Button
														style={{ width: '100%' }}
														onClick={async () => {
															await addRelation({
																relation: RELATIONS.AGENCY,
																type: 'Customer',
																sourceId: user?.ContentId,
																targetId: getUser()?.['Custom:User:Id'] || '',
																status: 'Rejected',
																query: `?reverse=true&emailTemplateId=GenericMessage&subject=${t(
																	'APP_MESSAGE_AGENCY_REJECT_SUBJECT',
																).replace(
																	'[YourName]',
																	currentUser?.ContentTitle || '',
																)}&message=${encodeURIComponent(
																	t('APP_MESSAGE_AGENCY_REJECT_BODY')
																		.replace(
																			'[RecipientName]',
																			user?.ContentTitle,
																		)
																		.replace(
																			'[YourName]',
																			currentUser?.ContentTitle || '',
																		),
																)}`,
															})
															await updateUser(user?.ContentId, [
																{
																	Name: 'Customer:Agency:Status',
																	Value: 'Rejected',
																},
															])
															setModal(null)
															await wait(1000)
															refresh()
															notif('success', t('APP_INVITE_REJECTED'))
														}}>
														{t('APP_REJECT')}
													</Button>
													<Button
														style={{ width: '100%' }}
														onClick={() => {
															setModal(null)
														}}>
														{t('APP_CANCEL')}
													</Button>
												</div>
											</>
										</div>
									),
								})
							}}
						/>
					))}
				</div>
			) : (
				<div className="responsiveGrid">
					<CreateCardPlaceholder
						label="APP_RESULT_NOT_FOUND"
						style={{
							minHeight: '214px',
						}}
					/>
				</div>
			)}
		</>
	)
}

export default AgencyInvites
